import { useContext, type ReactNode, type JSX} from "react";
import { Icon } from "../../components/ui";
import { schemaVisual } from "./schemaIcons";
import type { EntityRendererProps } from "../../runtime/contracts/agent";
import { entityHref, normalizeSchemaId } from "../../components/shared/entityHref";
import { AVATAR_COLOR_CLASSES } from "./sharedTypes";
import { ExpansionContext } from "../../components/agent/expansionContext";
import { ActionPrefix } from "./ActionPrefix";

/**
 * Standard entity card shell — ALL entity cards MUST use this.
 *
 * Provides:
 *   - <a href> navigation to /{module}/{type}/{id}
 *   - Module icon in module themeColor (left side)
 *   - children slot for custom content (right side)
 *
 * If no children, renders entity name.
 */
export function BaseEntityCard({
  schemaId,
  data,
  runtime,
  action,
  children,
}: EntityRendererProps & { readonly children?: ReactNode }): JSX.Element {
  const entityId = (data.id as string | undefined) ?? "";
  const href = entityHref(schemaId, entityId);
  const normalized = normalizeSchemaId(schemaId);
  const visual = schemaVisual(normalized);
  const mod = runtime.modules.get(visual.moduleId);
  const themeColor = mod?.themeColor;
  const iconClass = themeColor
    ? AVATAR_COLOR_CLASSES[themeColor]
    : "text-content-secondary";

  const Tag = href ? "a" : "span";
  const { bare, expanded } = useContext(ExpansionContext);
  const shellClass = bare
    ? `flex items-start gap-2.5 px-3 pt-2 text-inherit no-underline ${expanded ? "pb-0" : "pb-2"}`
    : "flex items-start gap-2.5 rounded-lg border border-edge bg-surface-tertiary px-3 py-2 text-inherit no-underline";

  return (
    <Tag
      {...(href ? { href } : {})}
      className={shellClass}
    >
      <span className={`flex h-[18px] shrink-0 items-center ${iconClass}`}>
        <Icon name={visual.icon} size={14} />
      </span>
      {children ?? (
        <span className="truncate text-sm text-content">
          <ActionPrefix action={action} />
          {(data.name as string | undefined) ?? (data.title as string | undefined) ?? "Untitled"}
        </span>
      )}
    </Tag>
  );
}
